import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_PREFIX, type ApiErrorDetail, type ApiMeta, type ErrorCode } from '@/domain';

/**
 * API client.
 *
 * Two responsibilities beyond plain HTTP:
 *   1. Unwrap the response envelope, so callers deal in data rather than `response.data.data`.
 *   2. Recover from an expired access token transparently (FR-AUTH-004) — one refresh call
 *      shared by every request that raced into a 401, then each original request replays.
 */

const baseURL = import.meta.env.VITE_API_BASE_URL || API_PREFIX;

export const http: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true, // the refresh token travels as an httpOnly cookie
  headers: { 'Content-Type': 'application/json' },
});

/** A normalised failure that UI code can branch on. */
export class ApiError extends Error {
  readonly code: ErrorCode | 'NETWORK_ERROR';
  readonly status: number;
  readonly details: ApiErrorDetail[];

  constructor(
    message: string,
    code: ErrorCode | 'NETWORK_ERROR',
    status: number,
    details: ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** True when the server rejected specific fields, so a form can highlight them. */
  get isValidation(): boolean {
    return this.code === 'VALIDATION_ERROR' && this.details.length > 0;
  }
}

// ---------------------------------------------------------------------------
// Access token — held in memory only.
//
// Not localStorage: a token in storage survives the tab and is readable by any script
// that gets injected. The refresh cookie is httpOnly and does the surviving instead.
// ---------------------------------------------------------------------------
let accessToken: string | null = null;
let onAuthFailure: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registered by AuthProvider so a dead session can clear app state and redirect. */
export function setAuthFailureHandler(handler: (() => void) | null): void {
  onAuthFailure = handler;
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

// ---------------------------------------------------------------------------
// Single-flight refresh.
//
// Several queries can 401 at the same moment when a token expires. Without this guard
// each would fire its own refresh, and token rotation would invalidate the others —
// logging the user out during what should be an invisible renewal.
// ---------------------------------------------------------------------------
export interface RefreshedSession {
  accessToken: string;
  user: {
    id: number;
    fullName: string;
    email: string;
    role: 'ADMIN' | 'STAFF';
    mustChangePassword: boolean;
  };
}

let refreshInFlight: Promise<RefreshedSession | null> | null = null;

/**
 * The single entry point for refreshing a session — used by the 401 interceptor AND by the
 * initial session probe on page load.
 *
 * Both must share this promise. The server rotates the refresh token on every use, so two
 * concurrent calls would send the same cookie, the first would consume it, and the second
 * would present an already-revoked token. React 18 StrictMode double-invokes effects in
 * development, which makes that race happen on *every* page load rather than rarely.
 */
export async function refreshSession(): Promise<RefreshedSession | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await axios.post<{ data: RefreshedSession }>(
        `${baseURL}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      setAccessToken(response.data.data.accessToken);
      return response.data.data;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      // Cleared on the next tick so every caller awaiting this promise observes the result.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

http.interceptors.response.use(
  (response) => response,
  async (
    error: AxiosError<{ error?: { code: ErrorCode; message: string; details?: ApiErrorDetail[] } }>,
  ) => {
    const original = error.config as RetriableConfig | undefined;

    if (!error.response) {
      throw new ApiError('Cannot reach the server. Check your connection.', 'NETWORK_ERROR', 0);
    }

    const { status } = error.response;
    let data:
      { error?: { code: ErrorCode; message: string; details?: ApiErrorDetail[] } } | undefined =
      error.response.data;
    const isRefreshCall = original?.url?.includes('/auth/refresh');

    // A failed blob request (a PDF) returns its JSON error body *as a Blob*, because
    // responseType was set before anyone knew the call would fail. Read it back, or the
    // user sees "Something went wrong" where the server said "Invoice not found".
    if (data instanceof Blob) {
      try {
        data = JSON.parse(await data.text());
      } catch {
        data = undefined;
      }
    }

    if (status === 401 && original && !original._retried && !isRefreshCall) {
      original._retried = true;
      const session = await refreshSession();

      if (session) {
        original.headers.set('Authorization', `Bearer ${session.accessToken}`);
        return http.request(original);
      }

      onAuthFailure?.();
    }

    throw new ApiError(
      data?.error?.message ?? 'Something went wrong',
      data?.error?.code ?? 'INTERNAL_ERROR',
      status,
      data?.error?.details ?? [],
    );
  },
);

// ---------------------------------------------------------------------------
// Envelope-unwrapping helpers. Every call site uses these rather than `http` directly.
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: boolean;
  data: T;
  meta?: ApiMeta;
}

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await http.get<Envelope<T>>(url, config);
  return response.data.data;
}

/** For list endpoints, where the pagination block matters as much as the rows. */
export async function apiGetList<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<{ data: T; meta?: ApiMeta }> {
  const response = await http.get<Envelope<T>>(url, config);
  return { data: response.data.data, ...(response.data.meta ? { meta: response.data.meta } : {}) };
}

/**
 * Fetches a binary document (a PDF) rather than JSON.
 *
 * It goes through `http` so it inherits the Authorization header and the 401-refresh retry:
 * a plain `window.open` on the PDF URL would carry no access token, since the token is held
 * in memory rather than in a cookie, and would come back 401.
 *
 * The filename comes from Content-Disposition — the server has already worked out the
 * document number, and the client should not be inventing a second naming scheme.
 */
export async function apiGetBlob(
  url: string,
  config?: AxiosRequestConfig,
): Promise<{ blob: Blob; filename: string }> {
  const response = await http.get<Blob>(url, { ...config, responseType: 'blob' });

  const disposition = String(response.headers['content-disposition'] ?? '');
  const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'document.pdf';

  return { blob: response.data, filename };
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.post<Envelope<T>>(url, body);
  return response.data.data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.patch<Envelope<T>>(url, body);
  return response.data.data;
}

export async function apiDelete<T>(url: string): Promise<T> {
  const response = await http.delete<Envelope<T>>(url);
  return response.data.data;
}
